import { RoleHelpers, useCommonComponentContext } from "@maysoft/common-component-react";
import { useEffect, useState } from "react";
import Helpers from "../../../commons/helpers";
import { IMessageTemplateCase } from "../../../commons/interfaces";
import { Constants } from "../../../constants/constants";
import { MessageTemplateType } from "../../../constants/enums";
import MessageTemplateCaseService, { IRequestGetPagedMessageTemplateCase } from "../../../services/message/MessageTemplateCase.service";
import useStrings from "../../useStrings";
import useServiceCodenames from "../../messageTemplate/useServiceCodenames";
import useTenantCodenames from "../../messageTemplate/useTenantCodenames";

interface IProps {
    requestGetPaged: IRequestGetPagedMessageTemplateCase;
    onGetPaged: (data: { query: string, totalCount: number }) => void;
}

interface IModel {
    requestData?: IRequestGetPagedMessageTemplateCase;
    requestDataTemp?: IRequestGetPagedMessageTemplateCase;
}

const useData = ({ requestGetPaged, onGetPaged }: IProps) => {
    const Strings = useStrings();

    const { userInfo, tenantCode, serviceCode, onError, onSuccess, onHideLoading, onShowLoading } = useCommonComponentContext();
    const isServiceAdmin = RoleHelpers.isServiceAdmin(userInfo.userProfile.roleType);
    const organizationId = userInfo.userProfile?.organizationId;
    const { serviceCodenames } = useServiceCodenames({ organizationId });
    const { tenantCodenames } = useTenantCodenames({ organizationId });

    const [model, setModel] = useState<IModel>({} as IModel);
    const [rows, setRows] = useState<IMessageTemplateCase[]>([]);

    const messageTemplateTypeData = [
        {
            code: MessageTemplateType.Email,
            name: Strings.Common.EMAIL,
        },
        {
            code: MessageTemplateType.SMS,
            name: Strings.Common.SMS,
        },
        {
            code: MessageTemplateType.Notification,
            name: Strings.Common.NOTIFICATION,
        },
    ];

    useEffect(() => {
        const newRequest: IRequestGetPagedMessageTemplateCase = {
            totalCount: requestGetPaged?.totalCount,
            orderby: requestGetPaged?.orderby || model?.requestData?.orderby,
            pageSize: requestGetPaged?.pageSize || model?.requestData?.pageSize,
            pageNumber: requestGetPaged?.pageNumber || model?.requestData?.pageNumber,

            caseCode: requestGetPaged?.caseCode || model?.requestData?.caseCode,
            searchText: requestGetPaged?.searchText || model?.requestData?.searchText,
            templateCode: requestGetPaged?.templateCode || model?.requestData?.templateCode,
            tenantCode: requestGetPaged?.tenantCode || model?.requestData?.tenantCode,
            serviceCode: requestGetPaged?.serviceCode || model?.requestData?.serviceCode,
        };

        getPaged(newRequest);
    }, [requestGetPaged, userInfo?.userProfile?.organizationId]);

    const getPaged = async (requestData?: IRequestGetPagedMessageTemplateCase) => {
        let tenantCodeRequest = tenantCode;
        let serviceCodeRequest = serviceCode;
        if (isServiceAdmin) {
            tenantCodeRequest = requestData?.tenantCode ?? undefined;
            serviceCodeRequest = requestData?.serviceCode ?? undefined;
        }

        try {
            onShowLoading();

            const pageSize = requestData?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(requestData?.pageNumber || 1, pageSize, requestData?.totalCount || 0);

            const newRequest = {
                pageSize: pageSize,
                pageNumber: pageNumber,
                searchText: requestData?.searchText,

                tenantCode: tenantCodeRequest,
                serviceCode: serviceCodeRequest,
                organizationId: organizationId || "0",
            };

            const result = await MessageTemplateCaseService.getPaged(newRequest);

            setRows([...(result.items || [])]);

            setModel({
                requestData: { ...newRequest, totalCount: result.totalCount || 0 },
                requestDataTemp: { ...newRequest, totalCount: result.totalCount || 0 },
            });

            let query: string = `?pageNumber=${pageNumber}&pageSize=${newRequest?.pageSize}`;
            if (!Helpers.isNullOrEmpty(newRequest?.searchText)) {
                query += `&searchText=${newRequest?.searchText}`;
            }
            if (!Helpers.isNullOrEmpty(newRequest?.tenantCode)) {
                query += `&tenantCode=${newRequest?.tenantCode}`;
            }
            if (!Helpers.isNullOrEmpty(newRequest?.serviceCode)) {
                query += `&serviceCode=${newRequest?.serviceCode}`;
            }

            onGetPaged({ query, totalCount: result.totalCount || 0 });
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };

    const handleDelete = async (id: string) => {
        Helpers.showConfirmAlert(Strings.MESSAGE_TEMPLATE_CASE.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                await MessageTemplateCaseService.delete(id);
                await getPaged({ ...model.requestData, pageNumber: 1 });

                onSuccess(Strings.MESSAGE_TEMPLATE_CASE.DELETE_SUCCESS);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    const onChangeValue = (value: string, key: keyof IRequestGetPagedMessageTemplateCase) => {
        setModel((prev) => ({
            ...prev,
            requestDataTemp: {
                ...prev.requestDataTemp,
                [key]: value,
            },
        }));
    };

    const onResetModel = () => setModel({ ...model, requestDataTemp: model.requestData });

    return {
        model,
        rows,
        handleDelete,
        onChangeValue,
        getPaged,
        onResetModel,
        isServiceAdmin,
        serviceCodenames,
        tenantCodenames,
    };
};

export default useData;
